import { useEffect, useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import v2_actionSyncUsuariosImoview from 'src/actions/v2/ai/v2_actionSyncUsuariosImoview';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Separator } from 'src/@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { MultiSelect } from 'src/@/components/ui/multi-select';

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

const PARCEIROS = [
    {
        value: 'imoview',
        label: 'Imoview',
        descricao: 'CRM da Universal Software',
        ondeObterToken: 'Use o email e a senha de um usuário Imoview (conta de serviço). A Lais abre os atendimentos em nome dele.',
    },
];

export default function EnvioAtendimentoSection({ aiAgent, changeModuleConfig }) {
    const { cliente } = useAuth();
    const realstate = aiAgent?.config?.module?.realstate || {};
    const funcoes = realstate.funcoes || {};
    const cfg = funcoes.envioAtendimento || {};
    const corretor = cfg.corretor || { tipo: 'fixo', usuarioId: null, usuariosRoleta: [] };

    const [revelarSenha, setRevelarSenha] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [sincronizando, setSincronizando] = useState(false);
    const [resultadoSync, setResultadoSync] = useState(null); // { ok, msg }

    const updateCfg = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                envioAtendimento: { ...cfg, ...patch },
            },
        });
    };

    const updateCorretor = (patch) => {
        updateCfg({ corretor: { ...corretor, ...patch } });
    };

    async function buscarUsuarios() {
        if (!cliente?._id) return;
        try {
            const result = await getActionListaUsuariosByClienteId(cliente._id);
            const lista = result?.data?.usuarios || [];
            setUsuarios(Array.isArray(lista) ? lista : []);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        buscarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente?._id]);

    // Só usuários LAIS já mapeados ao Imoview (têm config.imoview.codigoUsuario)
    // podem ser corretor responsável de um atendimento.
    const usuariosMapeados = useMemo(
        () => usuarios.filter((u) => u?.config?.imoview?.codigoUsuario != null),
        [usuarios],
    );

    const corretorOptions = useMemo(
        () => usuariosMapeados.map((u) => ({
            value: String(u._id),
            label: u.nomeFantasia || u.firstName || u.email || '(sem nome)',
        })),
        [usuariosMapeados],
    );

    async function sincronizar() {
        if (!aiAgent?._id) return;
        setSincronizando(true);
        setResultadoSync(null);
        try {
            const result = await v2_actionSyncUsuariosImoview(aiAgent._id, { email: cfg.email, senha: cfg.senha });
            const resumo = result?.data?.resumo;
            setResultadoSync({
                ok: true,
                msg: `${resumo?.casados ?? 0} usuário(s) mapeado(s) de ${resumo?.imoviewTotal ?? 0} do Imoview.`,
            });
            await buscarUsuarios();
        } catch (error) {
            const msg = error?.response?.data?.message || 'Falha ao sincronizar. Verifique email/senha.';
            setResultadoSync({ ok: false, msg });
        } finally {
            setSincronizando(false);
        }
    }

    const parceiroSelecionado = PARCEIROS.find((p) => p.value === cfg.parceiro);
    const tipoCorretor = corretor.tipo === 'roleta' ? 'roleta' : 'fixo';
    const ativoSemParceiro = !!cfg.ativo && !cfg.parceiro;
    const ativoSemCredenciais = !!cfg.ativo && !!cfg.parceiro && (!cfg.email || !cfg.senha);
    const ativoSemCorretorFixo = !!cfg.ativo && tipoCorretor === 'fixo' && !corretor.usuarioId;
    const ativoSemRoleta = !!cfg.ativo && tipoCorretor === 'roleta'
        && (!Array.isArray(corretor.usuariosRoleta) || corretor.usuariosRoleta.length === 0);
    const podeSincronizar = !!cfg.email && !!cfg.senha && !sincronizando;

    return (
        <Accordion
            type="single"
            collapsible
            className="rounded-xl border border-gray-100 bg-white"
        >
            <AccordionItem value="envio-atendimento" className="border-0">
                <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                    <div className="flex items-start justify-between gap-3 flex-1 pr-3 text-left">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                <Iconify icon="mdi:account-tie-outline" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">Envio de Atendimento ao CRM Parceiro</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    Quando todas as perguntas qualificatórias forem respondidas, a Lais abre um
                                    atendimento no CRM da imobiliária parceira e guarda o código do atendimento.
                                </div>
                            </div>
                        </div>
                        <div
                            role="presentation"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="shrink-0"
                        >
                            <Switch
                                checked={!!cfg.ativo}
                                onCheckedChange={(value) => updateCfg({ ativo: value })}
                            />
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                    {!cfg.ativo ? (
                        <p className="text-xs text-gray-500 italic">Ative o recurso acima para configurar.</p>
                    ) : (
                <div className="flex flex-col gap-4">
                        {ativoSemParceiro && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                <AlertDescription className="text-xs">
                                    Selecione um parceiro para a integração começar a funcionar.
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex flex-col gap-2 max-w-md">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Parceiro</Label>
                            <Select
                                value={cfg.parceiro || ''}
                                onValueChange={(value) => updateCfg({ parceiro: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder="Selecione um parceiro..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {PARCEIROS.map((p) => (
                                        <SelectItem key={p.value} value={p.value}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{p.label}</span>
                                                <span className="text-xs text-gray-500">{p.descricao}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {parceiroSelecionado && (
                            <>
                                <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                                    <AlertDescription className="text-xs">
                                        {parceiroSelecionado.ondeObterToken}
                                    </AlertDescription>
                                </Alert>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-2">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Email da conta Imoview
                                        </Label>
                                        <Input
                                            type="email"
                                            placeholder="conta@imobiliaria.com.br"
                                            value={cfg.email || ''}
                                            onChange={(e) => updateCfg({ email: e.target.value })}
                                            className={`${INPUT_CLS} ${ativoSemCredenciais && !cfg.email ? 'border-amber-300' : ''}`}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                            Senha
                                        </Label>
                                        <div className="relative">
                                            <Input
                                                type={revelarSenha ? 'text' : 'password'}
                                                placeholder="Senha da conta"
                                                value={cfg.senha || ''}
                                                onChange={(e) => updateCfg({ senha: e.target.value })}
                                                className={`${INPUT_CLS} pr-10 ${ativoSemCredenciais && !cfg.senha ? 'border-amber-300' : ''}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setRevelarSenha((v) => !v)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                tabIndex={-1}
                                            >
                                                <Iconify icon={revelarSenha ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <Separator className="bg-gray-200/60" />

                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                Corretor responsável
                                            </Label>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Sincronize os usuários do Imoview para poder selecioná-los aqui.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={sincronizar}
                                            disabled={!podeSincronizar}
                                            className={`flex items-center gap-2 px-3 py-2 text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 shrink-0 ${
                                                podeSincronizar ? 'bg-[#7C3AED] hover:bg-[#6d28d9] active:scale-95' : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                        >
                                            <Iconify icon={sincronizando ? 'eva:loader-outline' : 'eva:sync-outline'} width={14} className={sincronizando ? 'animate-spin' : ''} />
                                            {sincronizando ? 'Sincronizando...' : 'Sincronizar usuários'}
                                        </button>
                                    </div>

                                    {resultadoSync && (
                                        <Alert className={resultadoSync.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}>
                                            <AlertDescription className="text-xs">{resultadoSync.msg}</AlertDescription>
                                        </Alert>
                                    )}

                                    <RadioGroup
                                        value={tipoCorretor}
                                        onValueChange={(value) => updateCorretor({ tipo: value })}
                                        className="flex flex-col gap-2"
                                    >
                                        <label
                                            htmlFor="corretor-fixo"
                                            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                                tipoCorretor === 'fixo' ? 'border-[#7C3AED] bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <RadioGroupItem value="fixo" id="corretor-fixo" className="mt-1" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">Corretor fixo</span>
                                                <span className="text-xs text-gray-500">
                                                    Todos os atendimentos criados pela IA ficam com o mesmo corretor.
                                                </span>
                                            </div>
                                        </label>
                                        <label
                                            htmlFor="corretor-roleta"
                                            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                                                tipoCorretor === 'roleta' ? 'border-[#7C3AED] bg-violet-50/50' : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <RadioGroupItem value="roleta" id="corretor-roleta" className="mt-1" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-900">Roleta entre corretores</span>
                                                <span className="text-xs text-gray-500">
                                                    Distribui os atendimentos em rotação (round-robin) entre os corretores selecionados.
                                                </span>
                                            </div>
                                        </label>
                                    </RadioGroup>

                                    {corretorOptions.length === 0 && (
                                        <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                            <AlertDescription className="text-xs">
                                                Nenhum usuário mapeado ao Imoview ainda. Clique em "Sincronizar usuários".
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {tipoCorretor === 'fixo' && corretorOptions.length > 0 && (
                                        <div className="flex flex-col gap-2 max-w-md">
                                            <Select
                                                value={corretor.usuarioId ? String(corretor.usuarioId) : ''}
                                                onValueChange={(value) => updateCorretor({ usuarioId: value || null })}
                                            >
                                                <SelectTrigger className={INPUT_CLS}>
                                                    <SelectValue placeholder="Selecione um corretor..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {corretorOptions.map((c) => (
                                                        <SelectItem key={c.value} value={c.value}>
                                                            {c.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {ativoSemCorretorFixo && (
                                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                                    <AlertDescription className="text-xs">
                                                        Selecione um corretor responsável para os atendimentos.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                        </div>
                                    )}

                                    {tipoCorretor === 'roleta' && corretorOptions.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            <MultiSelect
                                                options={corretorOptions}
                                                onValueChange={(values) => updateCorretor({ usuariosRoleta: values })}
                                                defaultValue={Array.isArray(corretor.usuariosRoleta) ? corretor.usuariosRoleta.map(String) : []}
                                                placeholder="Selecione os corretores..."
                                                variant="inverted"
                                                animation={2}
                                                maxCount={5}
                                            />
                                            {ativoSemRoleta && (
                                                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                                    <AlertDescription className="text-xs">
                                                        Selecione pelo menos um corretor para a roleta.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                            {!ativoSemRoleta && (
                                                <p className="text-xs text-gray-500">
                                                    Cada novo atendimento é atribuído ao próximo corretor da lista, em rotação.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <Separator className="bg-gray-200/60" />

                        <Alert className="bg-violet-50 border-violet-200 text-violet-800">
                            <AlertDescription className="text-xs">
                                <strong>Idempotente:</strong> o atendimento é aberto uma única vez. O código do atendimento
                                retornado pelo parceiro fica guardado no lead para requisições futuras (interações,
                                agendamento de visita). Status e resposta ficam registrados no histórico do lead.
                            </AlertDescription>
                        </Alert>
                </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
